import { Button, Dropdown, Header, Icon, Input, Modal, Pagination, Table } from 'react-frontier';
import { useEffect, useRef, useState } from 'react';
import { AvailableItemForShipment, SetLoading } from '../SiteClasses';
import API from '../API';

interface SearchAvailableItemsModalProps{
	open: boolean,
	onClose: ()=>void,
	onSelected: (item: AvailableItemForShipment)=>void,
	onSave?: (setLoading: SetLoading)=>void,
	destination_zipcode: string,
	initialTypeId?: number,
	selectedItems?: AvailableItemForShipment[],
	requestedItems?: { type_id: number, type_name?: string, type_icon?: string, quantity: number }[],
}

var PAGE_SIZE = 4;

var SearchAvailableItemsModal = (props: SearchAvailableItemsModalProps)=>{
	var [selectedType, setSelectedType] = useState<number | null | undefined>(undefined);
	var [searching, setSearching] = useState<boolean>(false);
	var [searchInput, setSearchInput] = useState<string>('');
	var [items, setItems] = useState<AvailableItemForShipment[]>(null);
	var [error, setError] = useState<string>(null);
	var [sentQuery, setSentQuery] = useState<string>(null);
	var [page, setPage] = useState<number>(0);
	var inputRef = useRef<HTMLInputElement>(null);
	var zipcodeRef = useRef(props.destination_zipcode);
	zipcodeRef.current = props.destination_zipcode;

	useEffect(()=>{
		if(!props.open){
			setSearchInput('');
			setItems(null);
			setError(null);
			setSentQuery(null);
			setPage(0);
			setSelectedType(undefined);
			return;
		}
		setSelectedType(props.initialTypeId ?? null);
	}, [props.open]);

	useEffect(()=>{
		if(!props.open || selectedType === undefined) return;
		getItems();
	}, [props.open, selectedType]);

	var getItems = (query?: string, pageNum?: number)=>{
		setSearching(true);
		setError(null);
		var currentPage = pageNum ?? 0;
		setPage(currentPage);
		setSentQuery(query !== undefined ? query : null);
		API.getAvailableItemsForShipment(zipcodeRef.current, query || null, selectedType, currentPage * PAGE_SIZE, PAGE_SIZE).then(res=>{
			if(res.error) return setError(res.message);
			setItems(res.data || []);
		}).catch(()=>{
			setError('Hubo un error inesperado cargando los equipos.');
		}).finally(()=>{
			setSearching(false);
		});
	};

	var search = (setLoading?: SetLoading)=>{
		if(searching) return;
		getItems(searchInput);
	};

	var onPageChange = (p: number)=>{
		getItems(sentQuery ?? undefined, p);
	};

	var counters = props.requestedItems?.map(req=>{
		var selected = (props.selectedItems || []).filter(i => i.type_id === req.type_id).length;
		return { ...req, selected };
	});

	var isRowDisabled = (item: AvailableItemForShipment)=>{
		var isSelected = (props.selectedItems || []).some(s => s.item_id === item.item_id);
		if(isSelected) return false;
		if(counters){
			var counter = counters.find(c => c.type_id === item.type_id);
			if(counter && counter.selected >= counter.quantity) return true;
		}
		return false;
	};

	var onRowClick = (item: AvailableItemForShipment)=>{
		if(isRowDisabled(item)) return;
		props.onSelected(item);
	};

	var shownResults = items || [];
	if(props.requestedItems && props.requestedItems.length > 0 && !selectedType){
		var requestedTypeIds = props.requestedItems.map(r => r.type_id);
		shownResults = shownResults.filter(item => requestedTypeIds.includes(item.type_id));
	}
	var hasPagination = shownResults.length >= PAGE_SIZE || page !== 0;

	return <Modal open={props.open} onClose={props.onClose} size='normal'>
		<Modal.Header>
			Equipos Disponibles
			{counters && counters.length > 0 && (
				<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
					{counters.map(c=>(
						<div key={c.type_id} style={{ display: 'flex', alignItems: 'baseline', gap: 5, fontSize: 13 }}>
							<Icon name={c.type_icon as any} />
							<span>{c.type_name}</span>
							<span style={{
								fontWeight: 'bold',
								color: c.selected >= c.quantity ? 'green' : c.selected > 0 ? 'orange' : 'inherit'
							}}>
								{c.selected} / {c.quantity}
							</span>
						</div>
					))}
				</div>
			)}
		</Modal.Header>
		<Modal.Content style={{ maxHeight: 700, overflowY: 'auto', overflowX: 'hidden' }}>
			{!!props.open && (
				<>
					<Input
						ref={inputRef}
						value={searchInput}
						onChange={setSearchInput}
						placeholder='Buscar por ID, nombre, serie, tipo o ciudad...'
						inputType='text'
						button={<Button text='Buscar' onClick={search} loading={searching} />}
						onSubmit={search}
						submitOnEnter
					/>
						{props.requestedItems && props.requestedItems.length > 0 && (
						<Dropdown
							iconName='filter'
							value={selectedType ?? null}
							onValueChange={setSelectedType}
							items={[
								{ text: 'Todos los tipos', value: null },
								...props.requestedItems.map(t=>({ text: t.type_name || String(t.type_id), value: t.type_id }))
							]}
							style={{ marginTop: 10 }}
						/>
					)}
				</>
			)}
			{searching ? (
				<Header loading text='Cargando equipos' containerStyle={{ marginBottom: 50, marginTop: 50 }} />
			) : error ? (
				<Header text='Error' subtext={error} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : shownResults.length === 0 ? (
				<Header text='Sin resultados' subtext={'No se encontraron equipos' + (sentQuery ? ` con "${sentQuery}"` : '')} iconName='face-frown-open' style={{ marginTop: 50, marginBottom: 30 }} size='small' />
			) : (
				<Table
					fitted selectable striped divided
					style={{ marginTop: 20 }}
					className='first bottom round'
					headers={['', 'ID', 'Tipo', 'Nombre', 'Serie', 'Ubicación', 'Distancia']}
					collapsingIndexes={[0, 1, 2, 3, 6]}
					centeredIndexes={[0, 6]}
					footer={hasPagination && (
						<Table.Cell row colSpan={100}>
							<div style={{ justifyContent: 'flex-end', display: 'flex', marginBottom: 15 }}>
								<Pagination page={page} disabled={searching} onPageChange={onPageChange} hasNext={shownResults.length >= PAGE_SIZE} />
							</div>
						</Table.Cell>
					)}
				>
					{shownResults.map((item, i)=>{
						var disabled = isRowDisabled(item);
						var selected = (props.selectedItems || []).some(s => s.item_id === item.item_id);
						var locationCell = item.warehouse_name
							? <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}><Icon name='warehouse' /><span>{item.warehouse_name}</span></div>
							: item.location_city
								? <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}><Icon name='location-dot' /><span>{item.location_city}</span></div>
								: <Icon name='minus' color='gray' />;
						return (
							<Table.Row
								key={`AVAIL-${item.item_id}-${i}`}
								data={[
									<i className={`large icon circle ${selected ? 'check' : 'outline'}`}></i>,
									item.item_id,
									<div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
										<Icon name={item.type_icon as any} />
										<span>{item.type_name}</span>
									</div>,
									item.item_name,
									item.serial_number || <Icon name='minus' color='gray' />,
									locationCell,
									item.distance !== null && item.distance !== undefined
										? `${item.distance.toFixed(2)} km`
										: <Icon name='minus' color='gray' />,
								]}
								onClick={()=>onRowClick(item)}
								style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
							/>
						);
					})}
				</Table>
			)}
		</Modal.Content>
		<Modal.Actions>
			<Button text='Cerrar' basic onClick={props.onClose} />
			<Button text='Guardar' color='black' onClick={props.onSave || props.onClose} />
		</Modal.Actions>
	</Modal>;
};

export default SearchAvailableItemsModal;
